import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Network } from 'lucide-react';
import { verbund } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { mayAccess } from '../routeAccess';
import { useDateFormat } from '../lib/dates';
import { NETZ_NAME_VORGABE, VERBUND_PFAD } from '../lib/netz';

/**
 * Auf der Instanzseite einer eLibrary: ist sie als Knoten im Verlagsverbund
 * angemeldet, und wie steht es um ihn? Nur lesen.
 *
 * Die Zuordnung macht das Backend über die Hostnamen der Instanz (Haupt-
 * adresse und weitere Adressen) gegen die Knoten-Adressen der Control Plane.
 * Für andere Produkte kommt `elibrary: false` und die Karte bleibt weg; für
 * Rollen, die den Verbund nicht sehen dürfen, wird gar nicht erst gefragt.
 */
export default function VerbundKnotenKarte({ instanceId }: { instanceId: string }) {
  const { user } = useAuth();
  const darf = mayAccess(VERBUND_PFAD, user?.role);
  const { relative, dateTime } = useDateFormat();
  const q = useQuery({
    queryKey: ['verbund', 'instanz', instanceId],
    queryFn: () => verbund.instanz(instanceId),
    enabled: darf,
    staleTime: 60_000,
  });
  const d = q.data;
  if (!darf || !d?.elibrary) return null;
  const name = d.netzName ?? NETZ_NAME_VORGABE;

  return (
    <div className="rounded-xl border border-slate-200 bg-surface p-4">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Network size={15} /> {name}-Knoten
      </h3>
      {!d.eingerichtet ? (
        <p className="text-sm text-slate-500">Die Anbindung an die Control Plane ist im PC nicht eingerichtet.</p>
      ) : d.fehler ? (
        <p className="text-sm text-amber-700">{d.fehler}</p>
      ) : !d.knoten?.length ? (
        <p className="text-sm text-slate-500">
          Unter den Adressen dieser Instanz ist kein Knoten angemeldet. Teilnahme braucht ein Anmelde-Token der
          Control Plane (Feld „Anmelde-Token“ der Instanz).
        </p>
      ) : (
        <div className="space-y-2">
          {d.knoten.map((k) => (
            <div key={k.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="text-slate-500">Status</span>
              <span className="font-medium text-slate-800">
                {k.status === 'ACTIVE' ? 'aktiv' : k.status === 'SUSPENDED' ? 'ausgesetzt' : k.status === 'EXCLUDED' ? 'ausgeschlossen' : k.status}
                {k.participates ? '' : ' · nimmt nicht teil'}
              </span>
              <span className="text-slate-500">Verlag</span>
              <span className="text-slate-800">
                {k.verlag ? `${k.verlag.name} (${k.verlag.status})` : '—'}
              </span>
              <span className="text-slate-500">Letzter Abgleich</span>
              <span className="text-slate-800" title={dateTime(k.letzterAbgleich)}>
                {relative(k.letzterAbgleich)}
                {k.fehlerKlasse && <span className="ml-1 text-amber-700" title={k.abgleichFehler ?? ''}>· {k.fehlerKlasse}</span>}
                {k.eintraege !== null && <span className="ml-1 text-slate-500">· {k.eintraege} Einträge</span>}
              </span>
              <Link to={`${VERBUND_PFAD}?${new URLSearchParams({ reiter: 'verlage', q: k.verlag?.slug ?? k.baseUrl.replace(/^https?:\/\//, '') })}`} className="ml-auto text-xs text-brand-700 hover:underline">
                In der Verbund-Verwaltung öffnen
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
